import React, { useEffect, useState } from "react";
import PrimaryButton from "../../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../ui-library/Buttons/SecondaryButton/SecondaryButton";
import CustomDropdown from "../../../ui-library/CustomDropdown/CustomDropdown";
import styled, { withTheme } from "styled-components";
import PropTypes from "prop-types";
import LabelledInput from "../../../ui-library/LabelledInput/LabelledInput";
import { useTranslation } from "react-i18next";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 36px;
  align-items: center;
`;

const FieldTitle = styled.span`
  color: ${(props) => props.theme?.nero};
  font-weight: 600;
  font-size: 16px;
  margin-right: 14px;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledLabelledInput = styled(LabelledInput)`
  margin-top: 27px;
`;

const ChangeStudyStatus = (props) => {
  const { currentStatus, onSubmit, onCancel, processing, values } = props;

  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [updateReason, setUpdateReason] = useState("");

  const [updateReasonIsValid, setUpdateReasonIsValid] = useState(false);
  const [reasonForChangeError, setReasonForChangeError] = useState(null);

  useEffect(() => {
    setUpdateReasonIsValid(
      updateReason.replace(/\.|,/g, "").trim() !== "" &&
        updateReason.length <= 450
    );
  }, [updateReason]);

  const validateReason = (reason) => {
    setReasonForChangeError(false);
    if (reason.replace(/\.|,/g, "").trim() === "") {
      setReasonForChangeError(t("errorText.changeStudyStatus.reason.empty"));
    }
    if (reason.length > 450) {
      setReasonForChangeError(t("errorText.changeStudyStatus.reason.large"));
    }
  };

  const onSave = () => {
    if (selectedStatus) {
      const data = {
        studyStatus: selectedStatus,
        reason: updateReason,
      };
      onSubmit(data);
    }
  };

  const onChangeSelectedStatus = (value) => {
    const key = Object.keys(values).find(
      (statusKey) => values[statusKey].id === value.id
    );
    setSelectedStatus(values[key]);
  };

  return (
    <Container data-testid="change-study-status">
      {currentStatus && (
        <Row>
          <FieldTitle>
            {t("appText.changeStudyStatus.currentStatus")}:
          </FieldTitle>
          <CustomDropdown
            placeholder={currentStatus.value}
            disabled
            width={190}
          />
        </Row>
      )}

      <Row className="mb-5">
        <FieldTitle>{t("appText.changeStudyStatus.updateTo")}:</FieldTitle>
        <CustomDropdown
          placeholder={t("appText.changeStudyStatus.select")}
          values={Object.values(values)}
          width={190}
          onChange={onChangeSelectedStatus}
        />
      </Row>

      <StyledLabelledInput
        title={t("appText.changeStudyStatus.statusChangeReason")}
        type="text"
        data-testid="update-reason-input"
        value={updateReason}
        onChange={(e) => setUpdateReason(e.target.value)}
        onBlur={(e) => validateReason(e.target.value)}
        hasError={reasonForChangeError}
        errorMessage={reasonForChangeError}
      />

      {onSubmit && (
        <ActionButtonsContainer>
          {onCancel && (
            <SecondaryButton
              data-testid="cancel-button"
              className="mr-2"
              onClick={onCancel}
            >
              {t("appText.changeStudyStatus.cancel")}
            </SecondaryButton>
          )}
          <PrimaryButton
            data-testid="submit-button"
            disabled={!selectedStatus || !updateReasonIsValid || processing}
            onClick={onSave}
          >
            {t("appText.changeStudyStatus.save")}
          </PrimaryButton>
        </ActionButtonsContainer>
      )}
    </Container>
  );
};

ChangeStudyStatus.defaultProps = {
  onSubmit: null,
  currentStatus: null,
  hasError: false,
};

ChangeStudyStatus.propTypes = {
  currentStatus: PropTypes.object,
  onSubmit: PropTypes.func,
  hasError: PropTypes.bool,
  values: PropTypes.object.isRequired,
};

export default withTheme(ChangeStudyStatus);
